import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Chip } from "@/components/ui/chip";
import { TIME_OPTIONS } from "@/features/search/types";
import { cn } from "@/lib/cn";
import {
  formatHourLabel,
  getMaxDate,
  isBeyondMaxDate,
  isSameDay,
  parseISODate,
  toISODate,
} from "@/lib/dates";

interface WhenPanelProps {
  date: string | null;
  time: string | null;
  onDateChange: (date: string | null) => void;
  onTimeChange: (time: string | null) => void;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const TIME_LABELS: Record<string, string> = {
  any: "Any time",
  morning: `${formatHourLabel(9)} – ${formatHourLabel(12)}`,
  afternoon: `${formatHourLabel(12)} – ${formatHourLabel(17)}`,
  evening: `${formatHourLabel(17)} – ${formatHourLabel(0)}`,
};

function formatDate(d: Date): string {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
}

function WhenPanel({ date, time, onDateChange, onTimeChange }: WhenPanelProps) {
  const today = useMemo(() => new Date(), []);
  const tomorrow = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return d;
  }, [today]);
  const initialDate = parseISODate(date) ?? today;
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  const isToday = date === toISODate(today);
  const isTomorrow = date === toISODate(tomorrow);

  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    let startDow = firstOfMonth.getDay() - 1;
    if (startDow < 0) startDow = 6;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: { key: string; date: Date | null }[] = [];
    for (let i = 0; i < startDow; i++) cells.push({ key: `pre-${i}`, date: null });
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(viewYear, viewMonth, d);
      cells.push({ key: toISODate(dt), date: dt });
    }
    let trail = 0;
    while (cells.length % 7 !== 0) {
      cells.push({ key: `post-${trail++}`, date: null });
    }
    return cells;
  }, [viewYear, viewMonth]);

  const maxDate = useMemo(() => getMaxDate(), []);

  const canGoPrev = viewYear > today.getFullYear() || viewMonth > today.getMonth();
  const canGoNext =
    viewYear < maxDate.getFullYear() ||
    (viewYear === maxDate.getFullYear() && viewMonth < maxDate.getMonth());

  const prevMonth = () => {
    if (!canGoPrev) return;
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (!canGoNext) return;
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  };

  const isPast = (d: Date) => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return d < t;
  };

  const isDisabled = (d: Date) => isPast(d) || isBeyondMaxDate(d);

  const selectDate = (value: string | null) => {
    onDateChange(value);
    if (!value) onTimeChange(null);
  };

  const selectTime = (value: string | null) => {
    if (value && value !== "any" && !parseISODate(date)) onDateChange(toISODate(today));
    onTimeChange(value);
  };

  return (
    <div className="flex gap-0">
      {/* Left: quick picks */}
      <div className="flex w-[180px] shrink-0 flex-col gap-2 border-r border-fog p-4">
        <QuickPickCard
          label="Today"
          sublabel={formatDate(today)}
          active={isToday}
          onClick={() => selectDate(isToday ? null : toISODate(today))}
        />
        <QuickPickCard
          label="Tomorrow"
          sublabel={formatDate(tomorrow)}
          active={isTomorrow}
          onClick={() => selectDate(isTomorrow ? null : toISODate(tomorrow))}
        />
      </div>

      {/* Right: calendar + time */}
      <div className="flex-1 p-4">
        {/* Calendar header */}
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            aria-label="Previous month"
            onClick={prevMonth}
            disabled={!canGoPrev}
            className="rounded-xs p-1 text-slate hover:text-ink disabled:opacity-30"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span aria-live="polite" className="text-[14px] font-semibold">
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <button
            type="button"
            aria-label="Next month"
            onClick={nextMonth}
            disabled={!canGoNext}
            className="rounded-xs p-1 text-slate hover:text-ink disabled:opacity-30"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>

        {/* Day labels */}
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {DAY_LABELS.map((label) => (
            <div key={label} className="py-1 text-[11px] font-medium text-slate">
              {label}
            </div>
          ))}
          {/* fallow-ignore-next-line complexity */}
          {calendarDays.map((cell) => {
            if (!cell.date) return <div key={cell.key} className="py-1.5" />;
            const day = cell.date;
            const disabled = isDisabled(day);
            const selected = date === toISODate(day);
            const todayHighlight = isSameDay(day, today);
            return (
              <button
                key={cell.key}
                type="button"
                aria-label={day.toLocaleDateString("en-GB", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
                aria-pressed={selected}
                aria-current={todayHighlight ? "date" : undefined}
                disabled={disabled}
                className={cn(
                  "rounded-full py-1.5 text-[13px] transition-colors",
                  disabled && "cursor-not-allowed text-slate/40",
                  !disabled && !selected && "hover:bg-fog",
                  selected &&
                    "bg-electric-50 font-semibold text-electric-800 ring-1 ring-inset ring-electric-700",
                  todayHighlight && !selected && "font-semibold ring-1 ring-inset ring-fog-dark",
                )}
                onClick={() => selectDate(selected ? null : toISODate(day))}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-[12px] text-slate">Dates and times are local to each salon.</p>

        {/* Time selection */}
        <div className="mt-4 border-t border-fog pt-3">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-[13px] font-medium text-slate">Select time</span>
            <div className="flex flex-wrap gap-1.5">
              {TIME_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  variant={time === opt.value ? "active" : "default"}
                  aria-pressed={time === opt.value}
                  onClick={() => selectTime(time === opt.value ? null : opt.value)}
                  className="h-7 px-3 text-[12px]"
                >
                  <span>{opt.label}</span>
                  {TIME_LABELS[opt.value] && opt.value !== "any" && (
                    <span className="ml-0.5 text-[10px]">{TIME_LABELS[opt.value]}</span>
                  )}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickPickCard({
  label,
  sublabel,
  active,
  onClick,
}: {
  label: string;
  sublabel: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        "flex flex-col items-center gap-0.5 rounded-xl border px-4 py-3.5 text-center transition-all",
        active
          ? "border-electric-700 bg-electric-50 font-medium"
          : "border-fog hover:border-fog-dark hover:bg-fog/40",
      )}
      onClick={onClick}
    >
      <span className={cn("text-[14px] font-semibold", active ? "text-electric-800" : "text-ink")}>
        {label}
      </span>
      <span className={cn("text-[12px]", active ? "text-electric-800" : "text-slate")}>
        {sublabel}
      </span>
    </button>
  );
}

export { WhenPanel, type WhenPanelProps };
